import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  InputNumber,
  DatePicker,
  message,
  Space,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';

const { Option } = Select;

const VanCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const formattedValues = {
        ...values,
        lastMaintenance: values.lastMaintenance?.toISOString(),
        nextService: values.nextService?.toISOString(),
      };
      
      await apiService.vans.create(formattedValues);
      message.success('Van created successfully');
      navigate('/vans');
    } catch (error) {
      console.error('Error creating van:', error);
      message.error(error.response?.data?.message || 'Failed to create van');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card
        title={
          <Space>
            <CarOutlined />
            <span>Add New Van</span>
          </Space>
        }
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/vans')}
          >
            Back to List
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: 'available',
            vanType: 'company_van'
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="registration"
                label="Registration Number"
                rules={[{ required: true, message: 'Please enter registration number' }]}
              >
                <Input prefix={<CarOutlined />} placeholder="Enter registration number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="vanType"
                label="Van Type"
                rules={[{ required: true, message: 'Please select van type' }]}
              >
                <Select placeholder="Select van type">
                  <Option value="company_van">Company Van</Option>
                  <Option value="rental">Rental</Option>
                  <Option value="finance">Finance</Option>
                  <Option value="contract_van">Contract Van</Option>
                  <Option value="external_van">External Van</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="available">Available</Option>
                  <Option value="in-use">In Use</Option>
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="not available">Not Available</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="make"
                label="Make"
                rules={[{ required: true, message: 'Please enter make' }]}
              >
                <Input placeholder="Enter make" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[{ required: true, message: 'Please enter model' }]}
              >
                <Input placeholder="Enter model" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Year"
                rules={[{ required: true, message: 'Please enter year' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1900}
                  max={new Date().getFullYear()}
                  placeholder="Enter year"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="capacity"
                label="Capacity"
                rules={[{ required: true, message: 'Please enter capacity' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Enter capacity"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>
              <ToolOutlined />
              <span>Maintenance Information</span>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="currentMileage"
                label="Current Mileage"
                rules={[{ required: true, message: 'Please enter current mileage' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Enter current mileage"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="lastMaintenance"
                label="Last Maintenance"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select last maintenance date"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="nextService"
                label="Next Service"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select next service date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea
              rows={4}
              placeholder="Enter any additional notes"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Create Van
              </Button>
              <Button onClick={() => navigate('/vans')}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VanCreate; 